import React, { useState } from 'react';
import { Download, UploadCloud, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import { inventoryService } from '../../../services/inventoryService';
import {
  parseGreenBeanImportFile,
  MAX_IMPORT_ROWS,
} from '../../../utils/greenBeanImportParser';
import { Button } from '../../common/Button';
import { cn } from '../../../utils/cn';
import type { ImportRow } from './types';

interface UploadStepProps {
  onParsed: (rows: ImportRow[]) => void;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024;

export const UploadStep: React.FC<UploadStepProps> = ({ onParsed }) => {
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = async (file: File) => {
    setError(null);
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setError(t('inventory.import.errors.notXlsx'));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError(t('inventory.import.errors.tooLarge'));
      return;
    }
    setBusy(true);
    try {
      const { rows } = await parseGreenBeanImportFile(file);
      if (rows.length === 0) {
        setError(t('inventory.import.errors.missingHeader'));
        return;
      }
      onParsed(rows);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Parse error';
      if (/too many/i.test(msg)) setError(t('inventory.import.errors.tooManyRows'));
      else if (/missing/i.test(msg)) setError(t('inventory.import.errors.missingHeader'));
      else setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setError(null);
    setDownloadingTemplate(true);
    try {
      await inventoryService.downloadGreenBeanImportTemplate();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="h-5 w-5 text-primary-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              Start with the import template
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Pre-built columns with Species and Process Type dropdowns.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          isLoading={downloadingTemplate}
          leftIcon={<Download className="h-4 w-4" />}
          data-test-id="import-green-bean-download-template"
        >
          {t('inventory.import.downloadTemplate')}
        </Button>
      </div>

      <label
        data-test-id="import-green-bean-drop-zone"
        className={cn(
          'block rounded-lg border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-all',
          isDragging
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50',
          busy && 'opacity-60 pointer-events-none',
        )}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const f = e.dataTransfer.files[0];
          if (f) void handleFiles(f);
        }}
      >
        <div
          className={cn(
            'mx-auto flex h-12 w-12 items-center justify-center rounded-full transition-colors',
            isDragging ? 'bg-primary-100' : 'bg-gray-100',
          )}
        >
          <UploadCloud
            className={cn(
              'h-6 w-6 transition-colors',
              isDragging ? 'text-primary-600' : 'text-gray-500',
            )}
          />
        </div>
        <p className="mt-3 text-sm font-medium text-gray-900">
          {t('inventory.import.uploadHint')}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          .xlsx · max {MAX_IMPORT_ROWS} rows · max 5&nbsp;MB
        </p>
        <input
          data-test-id="import-green-bean-file-input"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            // Clear the input value first so re-selecting the same file
            // still triggers onChange next time.
            e.target.value = '';
            if (f) void handleFiles(f);
          }}
        />
      </label>

      {busy && (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary-500 animate-pulse" />
          {t('inventory.import.uploadParsing')}
        </div>
      )}

      {error && (
        <div
          data-test-id="import-green-bean-upload-error"
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
